import React from "react";
import {
  CpSelectMulti,
  CpSelectMultiProps,
} from "../cp-select-multi.component";
import {
  useController,
  FieldValues,
  Control,
  UseControllerProps,
  FieldPath,
  FieldPathValue,
  RegisterOptions,
} from "react-hook-form";

export type CpSelectMultiFieldProps<
  TData,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
  CpSelectMultiProps<TData, FieldPathValue<TFieldValues, TName>[number]>,
  "value" | "onChange" | "onBlur" | "error"
> & {
  fieldName: TName;
  control: Control<TFieldValues>;
  controllerOptions?: Omit<
    UseControllerProps<TFieldValues, TName>,
    "name" | "control" | "rules"
  >;
  required?: boolean;
  rules?: RegisterOptions<TFieldValues, TName>;
};

export function CpSelectMultiField<
  TData,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: CpSelectMultiFieldProps<TData, TFieldValues, TName>) {
  const {
    fieldName,
    control,
    controllerOptions,
    required,
    rules = {},
    data,
    transformData,
    ...cpInputMultiProps
  } = props;

  const mergedRules: RegisterOptions<TFieldValues, TName> = {
    ...rules,
    validate: {
      ...rules?.validate,
    },
  };
  if (required) {
    mergedRules.validate = {
      ...mergedRules.validate,
      isRequired: (v: unknown) => {
        return (Array.isArray(v) && v.length > 0) || "Field is required";
      },
    };
  }

  const {
    field: { value, onBlur, onChange, ref },
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
    rules: mergedRules,
    ...controllerOptions,
  });

  return (
    <CpSelectMulti<TData, FieldPathValue<TFieldValues, TName>[number]>
      {...cpInputMultiProps}
      data={data}
      transformData={transformData}
      error={error?.message}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
      triggerRef={ref as any}
    />
  );
}
